import React, { createContext, useContext, useState, useEffect } from 'react';

// Import all language files
import en from '../locales/en.json';
import ru from '../locales/ru.json';
import vi from '../locales/vi.json';
import zh from '../locales/zh.json';
import fr from '../locales/fr.json';

const languages = {
  en,
  ru,
  vi,
  zh,
  fr
};

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  // Load saved language preference or default to English
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    const saved = localStorage.getItem('musicxml-player-language');
    return saved && languages[saved] ? saved : 'en';
  });

  // Save language preference when it changes
  useEffect(() => {
    localStorage.setItem('musicxml-player-language', currentLanguage);
  }, [currentLanguage]);

  // Translation function with interpolation support
  const translate = (key, params = {}) => {
    const keys = key.split('.');
    let value = languages[currentLanguage];
    
    // Navigate through nested object
    for (const k of keys) {
      value = value?.[k];
    }
    
    // If translation not found, fallback to English
    if (value === undefined) {
      value = languages.en;
      for (const k of keys) {
        value = value?.[k];
      }
    }
    
    // If still not found, return the key itself
    if (value === undefined) {
      return key;
    }
    
    // Replace parameters in string
    if (typeof value === 'string' && Object.keys(params).length > 0) {
      return value.replace(/\{(\w+)\}/g, (match, paramKey) => {
        return params[paramKey] !== undefined ? params[paramKey] : match;
      });
    }
    
    return value;
  };

  // Short alias for translate
  const t = translate;

  const changeLanguage = (languageCode) => {
    if (languages[languageCode]) {
      setCurrentLanguage(languageCode);
    }
  };

  // Function to save language preference to user profile
  const saveLanguageToProfile = async (languageCode, userId) => {
    if (!userId) {
      // console.log('LanguageContext: No userId provided, not saving language preference');
      return;
    }
    
    // console.log('LanguageContext: Saving language preference:', languageCode, 'for user:', userId);
    
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const url = `${API_BASE}/api/user?operation=updateAccount&userId=${userId}`;
      const body = JSON.stringify({ languagePreference: languageCode });
      
      // console.log('LanguageContext: Making API request to:', url);
      // console.log('LanguageContext: Request body:', body);
      
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: body,
      });

      // console.log('LanguageContext: API response status:', response.status);
      
      if (response.ok) {
        const result = await response.json();
        // console.log('LanguageContext: Language preference saved successfully:', result);
      } else {
        const errorData = await response.json();
        console.error('LanguageContext: Failed to save language preference:', response.status, errorData);
      }
    } catch (error) {
      console.error('LanguageContext: Error saving language preference:', error);
    }
  };

  // Function to load language from user profile
  const loadLanguageFromProfile = async (userId) => {
    if (!userId) return null;
    
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${API_BASE}/api/user?operation=getAccount&userId=${userId}`);
      
      if (response.ok) {
        const data = await response.json();
        return data.languagePreference || 'en';
      }
    } catch (error) {
      console.error('Error loading language preference:', error);
    }
    return null;
  };

  const getAvailableLanguages = () => {
    return [
      { code: 'en', name: translate('language.english') },
      { code: 'ru', name: translate('language.russian') },
      { code: 'vi', name: translate('language.vietnamese') },
      { code: 'zh', name: translate('language.chinese') },
      { code: 'fr', name: translate('language.french') }
    ];
  };

  const getCurrentLanguageInfo = () => {
    const languages = getAvailableLanguages();
    return languages.find(lang => lang.code === currentLanguage) || languages[0];
  };

  const value = {
    currentLanguage,
    translate,
    t, // Short alias
    changeLanguage,
    saveLanguageToProfile,
    loadLanguageFromProfile,
    getAvailableLanguages,
    getCurrentLanguageInfo
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;